import React, { useState, useContext, useRef, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InfoIcon from '@mui/icons-material/Info';

import { AuthContext } from '../../context/auth-context';
import PackagesAPI from '../../../services/api/admin/packages-api';
import { BudgetChangeReasonsDialog } from './BudgetChangeReasonsDialog';
import { accStopMessage, stripAccPrefix } from './accAssetMessages';
import Logger from '../../../utils/logger';

const STEPS = ['Upload File', 'Preview & Resolve', 'Import Results'];

// Ticket #257: after a commit, ACC assets are created by a background run on the
// server. The results step polls its status until it finishes.
const ACC_POLL_INTERVAL_MS = 5000;
const ACC_POLL_MAX_MS = 60 * 60 * 1000;

const ResultTile = ({ label, value, color }) => (
  <Box textAlign="center" flex={1}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" color={color}>
      {value}
    </Typography>
  </Box>
);

/**
 * ACC asset creation after a commit: progress while the background run is
 * going, then its results, why it stopped (if it did) and the packages ACC
 * refused.
 */
const AccAssetProgress = ({ accAssets, status, statusError, timedOut }) => {
  const notStarted = accAssets.worker === 'not_started';
  const joinedRun = accAssets.worker === 'already_running';
  const progress = status?.progress;
  // Until the first status arrives, assume the run the server started is going.
  const running = status ? status.running : !notStarted;
  const done = progress
    ? progress.created + progress.linked + progress.failed
    : 0;
  const percent = Math.min(
    100,
    Math.round((done / Math.max(accAssets.pending, 1)) * 100)
  );
  const stopMessage = !running && accStopMessage(progress);
  const failedCount = status?.counts?.failed || 0;
  const failures = status?.failures || [];
  const packagesLabel = (n) => `${n} ${n === 1 ? 'package' : 'packages'}`;

  return (
    <Box maxWidth={500} mx="auto" textAlign="left" mb={2}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        ACC Assets
      </Typography>

      {notStarted && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
          ACC asset creation could not be started. The{' '}
          {packagesLabel(accAssets.pending)} are pending; use &quot;Create
          pending ACC assets&quot; on the Packages page.
        </Alert>
      )}

      {!notStarted && running && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <LinearProgress
            variant={joinedRun || !progress ? 'indeterminate' : 'determinate'}
            value={percent}
            sx={{ mb: 1.5 }}
          />
          <Typography variant="body2">
            {joinedRun
              ? `An ACC asset run was already going for this project; your ${packagesLabel(accAssets.pending)} were added to it.`
              : `Creating ACC assets: ${Math.min(done, accAssets.pending)} of ${accAssets.pending}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can close this window — this keeps running.
          </Typography>
        </Paper>
      )}

      {!running && progress && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            gap: 2,
            backgroundColor: 'grey.50',
          }}
        >
          <ResultTile
            label="Created in ACC"
            value={progress.created}
            color="success.main"
          />
          <ResultTile
            label="Already in ACC"
            value={progress.linked}
            color="info.main"
          />
          <ResultTile
            label="Failed"
            value={progress.failed}
            color={progress.failed ? 'error.main' : 'text.secondary'}
          />
        </Paper>
      )}

      {/* The server restarted, so the run's progress is gone. */}
      {!running && status && !progress && status.counts.pending > 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
          {packagesLabel(status.counts.pending)} in this project{' '}
          {status.counts.pending === 1 ? 'is' : 'are'} waiting for an ACC asset.
          Use &quot;Create pending ACC assets&quot; on the Packages page.
        </Alert>
      )}

      {stopMessage && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
          {stopMessage}
        </Alert>
      )}

      {!running && failedCount > 0 && (
        <Box mb={2}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            ACC refused {packagesLabel(failedCount)} in this project
            {failedCount > failures.length
              ? ` (first ${failures.length} shown)`
              : ''}
            :
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 200 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failures.map((failure) => (
                  <TableRow key={failure.packageId}>
                    <TableCell>{failure.name || '-'}</TableCell>
                    <TableCell>{failure.description || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="error">
                        {stripAccPrefix(failure.error)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary">
            Fix these packages, then use &quot;Create pending ACC assets&quot;
            on the Packages page to try again.
          </Typography>
        </Box>
      )}

      {statusError && running && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
          {statusError}
        </Alert>
      )}

      {timedOut && running && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
          ACC assets are still being created after an hour. Progress is no
          longer shown here; check the Packages page.
        </Alert>
      )}
    </Box>
  );
};

export const CsvPackageModal = ({
  open,
  onClose,
  onUploadComplete,
  projectId,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Validation Results State
  const [validationResults, setValidationResults] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);

  // Preview table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Commit Results State
  const [commitResults, setCommitResults] = useState(null);

  // Budget Change Reasons dialog (ticket #160 addendum). Its own error state so
  // a rejected commit keeps the dialog open with the reason the user typed,
  // rather than surfacing behind it on the preview step.
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetError, setBudgetError] = useState(null);

  // Background ACC asset run after the commit (ticket #257)
  const [accStatus, setAccStatus] = useState(null);
  const [accStatusError, setAccStatusError] = useState(null);
  const [accPollTimedOut, setAccPollTimedOut] = useState(false);

  // "Delete these packages?" after an import brings budgets to zero (ticket #268)
  const [zeroedDialogOpen, setZeroedDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
  const [bulkDeleteError, setBulkDeleteError] = useState(null);

  const { token } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  // Poll the ACC asset run while the results step is open. Stops when the run
  // finishes, after an hour, or when the modal closes (the cleanup clears the
  // timer); the run itself carries on on the server.
  const accPending = commitResults?.acc_assets?.pending || 0;

  const zeroedPackages = commitResults?.zeroed_budget_packages || [];
  // "Autodesk name - package name" when an Autodesk name is set.
  const zeroedPackageLabel = (p) => {
    const autodeskName = p?.autodesk_name && String(p.autodesk_name).trim();
    if (!p?.name) return autodeskName || '';
    return autodeskName ? `${autodeskName} - ${p.name}` : p.name;
  };
  // Blocked results carry only ids; names come from the import's list.
  const zeroedNameById = new Map(
    zeroedPackages.map((p) => [p.id, zeroedPackageLabel(p)])
  );
  // Deleted packages whose ACC asset was left live by the dev/staging guard
  const envGuardSkipped =
    bulkDeleteResult?.deleted.filter((d) => d.accSkipReason === 'env_guard')
      .length || 0;
  const accWorkerStarted =
    !!commitResults?.acc_assets &&
    commitResults.acc_assets.worker !== 'not_started';
  useEffect(() => {
    if (!open || activeStep !== 2 || accPending === 0 || !accWorkerStarted) {
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const status = await PackagesAPI.getAccAssetStatus(projectId, token);
        if (cancelled) return;
        setAccStatus(status);
        setAccStatusError(null);
        if (!status.running) return;
      } catch (err) {
        if (cancelled) return;
        // Keep polling: a missed status call does not stop the run.
        Logger.error('ACC asset status error', err);
        setAccStatusError(
          'Could not load ACC asset progress. Still trying — the assets are being created on the server.'
        );
      }
      if (Date.now() - startedAt >= ACC_POLL_MAX_MS) {
        setAccPollTimedOut(true);
        return;
      }
      timer = setTimeout(poll, ACC_POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, activeStep, accPending, accWorkerStarted, projectId, token]);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (
        droppedFile.name.endsWith('.csv') ||
        droppedFile.type === 'text/csv'
      ) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Invalid file type. Please upload a CSV file.');
      }
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.name.endsWith('.csv') ||
        selectedFile.type === 'text/csv'
      ) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Invalid file type. Please upload a CSV file.');
        setFile(null);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Step 1: Validate file (Dry-run)
  const handleValidate = async () => {
    if (!file) {
      setError('Please select or drop a CSV file first.');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      Logger.userAction('Initiating CSV dry-run validation', {
        fileName: file.name,
        projectId,
      });
      const results = await PackagesAPI.validateCsv(projectId, formData, token);

      setValidationResults(results);

      // Initialize row actions:
      // - WILL_CREATE defaults to CREATE
      // - WILL_UPDATE defaults to UPDATE, but only when the row actually has
      //   changes; unchanged update rows default to SKIP (nothing to write)
      // - DUPLICATE defaults to SKIP
      // - ERROR defaults to SKIP
      const initializedRows = (results.rows || []).map((row) => {
        let defaultAction = 'SKIP';
        if (row.status === 'WILL_CREATE') defaultAction = 'CREATE';
        else if (row.status === 'WILL_UPDATE') {
          defaultAction =
            row.changed_fields && row.changed_fields.length > 0
              ? 'UPDATE'
              : 'SKIP';
        }

        return {
          ...row,
          selectedAction: defaultAction,
        };
      });

      setPreviewRows(initializedRows);
      setPage(0);
      setActiveStep(1);
    } catch (err) {
      Logger.error('CSV validation error', err);
      const data = err.response?.data;
      const message =
        (data && typeof data === 'object' && data.message) ||
        (typeof data === 'string' ? data : null);
      setError(
        message ||
          'Validation failed. Please verify your CSV headers and data format.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle action change for individual row in Step 2
  const handleRowActionChange = (rowNum, newAction) => {
    setPreviewRows((prevRows) =>
      prevRows.map((row) =>
        row.row_number === rowNum ? { ...row, selectedAction: newAction } : row
      )
    );
  };

  // Download error report as CSV
  const handleDownloadErrorReport = () => {
    if (!previewRows.length) return;

    Logger.userAction('Downloading validation error report', { projectId });
    const headers = [
      'Row Number',
      'Description',
      'Name',
      'Status',
      'Errors/Messages',
    ];
    const csvContent = [
      headers.join(','),
      ...previewRows.map((r) => {
        const errorsStr = r.errors ? r.errors.join('; ') : '';
        return [
          r.row_number,
          `"${(r.name || '').replace(/"/g, '""')}"`,
          `"${(r.package_identifier || r.data?.name || '').replace(/"/g, '""')}"`,
          r.status,
          `"${errorsStr.replace(/"/g, '""')}"`,
        ].join(',');
      }),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `validation_report_${file?.name || 'packages'}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Budget changes about to be written (ticket #160 addendum). Detected by the
  // validation step per row, then filtered here to the rows actually being
  // committed — a row the user switched to SKIP must not be attributed a reason.
  const pendingBudgetChanges = useMemo(
    () =>
      previewRows
        .filter((row) => row.selectedAction === 'UPDATE' && row.budget_change)
        .map((row) => row.budget_change),
    [previewRows]
  );

  // Step 2: Commit changes. The server re-detects budget changes inside the
  // transaction and rejects the whole upload if a reason is missing, so this is
  // the prompt, not the guarantee.
  const runCommit = async (budgetChangeReason) => {
    setLoading(true);
    setError(null);
    setBudgetError(null);

    // Prepare payload
    const commitPayload = {
      mode: validationResults.mode,
      rows: previewRows.map((row) => ({
        row_number: row.row_number,
        action: row.selectedAction,
        existing_package_id: row.existing_package_id,
        data: row.data,
      })),
    };
    if (budgetChangeReason) {
      commitPayload.budget_change_reason = budgetChangeReason;
    }

    try {
      Logger.userAction('Committing CSV transactions to database', {
        projectId,
        mode: validationResults.mode,
        rowCount: commitPayload.rows.length,
        budgetChanges: pendingBudgetChanges.length,
      });
      const results = await PackagesAPI.commitCsv(
        projectId,
        commitPayload,
        token
      );
      setCommitResults(results);
      setBudgetDialogOpen(false);
      setActiveStep(2);
      // Budgeted hours block deletion, so zeroing them is usually the step
      // right before deleting — offer it now.
      if (results?.zeroed_budget_packages?.length > 0) {
        setZeroedDialogOpen(true);
      }
    } catch (err) {
      Logger.error('CSV commit error', err);
      const data = err.response?.data;
      const message =
        (data && typeof data === 'object' && data.message) ||
        (typeof data === 'string' ? data : null);
      const displayMessage =
        message ||
        'Failed to commit transaction. Entire operation rolled back.';
      // Keep the failure where the user is looking.
      if (budgetChangeReason) {
        setBudgetError(displayMessage);
      } else {
        setError(displayMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = () => {
    if (pendingBudgetChanges.length > 0) {
      setBudgetError(null);
      setBudgetDialogOpen(true);
      return;
    }
    runCommit();
  };

  // Cancelling the reasons dialog abandons the whole upload — nothing has been
  // committed at this point. The dialog confirms with the user before calling.
  const handleBudgetDialogCancel = () => {
    setBudgetDialogOpen(false);
    handleClose();
  };

  // Each package goes through the same checks as a single delete; any still
  // blocked (e.g. tracked time) are listed with the reason and left in place.
  const handleBulkDeleteZeroed = async () => {
    const ids = commitResults.zeroed_budget_packages.map((p) => p.id);
    setBulkDeleting(true);
    setBulkDeleteError(null);
    Logger.userAction('Bulk deleting zero-budget packages after CSV import', {
      count: ids.length,
    });
    try {
      const result = await PackagesAPI.bulkDelete(ids, token);
      setBulkDeleteResult(result);
    } catch (err) {
      Logger.error('Bulk delete after CSV import failed', err);
      setBulkDeleteError(
        err.response?.data?.message ||
          err.message ||
          'Could not delete the packages.'
      );
    } finally {
      setBulkDeleting(false);
      setZeroedDialogOpen(false);
    }
  };

  // Close and Cleanup
  const handleClose = () => {
    const completedResults = activeStep === 2 ? commitResults : null;

    // Reset state
    setActiveStep(0);
    setFile(null);
    setValidationResults(null);
    setPreviewRows([]);
    setCommitResults(null);
    setError(null);
    setBudgetDialogOpen(false);
    setBudgetError(null);
    setPage(0);
    setAccStatus(null);
    setAccStatusError(null);
    setAccPollTimedOut(false);
    setZeroedDialogOpen(false);
    setBulkDeleteResult(null);
    setBulkDeleteError(null);

    onClose();

    // Trigger list refresh + result toast only when closing committed results
    if (completedResults && onUploadComplete) {
      onUploadComplete(completedResults);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setCommitResults(null);
    setError(null);
  };

  const renderStatusChip = (status) => {
    switch (status) {
      case 'WILL_CREATE':
        return (
          <Chip
            size="small"
            label="Will Create"
            color="success"
            variant="outlined"
          />
        );
      case 'WILL_UPDATE':
        return (
          <Chip
            size="small"
            label="Will Update"
            color="success"
            variant="outlined"
          />
        );
      case 'DUPLICATE':
        return (
          <Chip
            size="small"
            label="Duplicate Name"
            color="warning"
            variant="outlined"
          />
        );
      case 'ERROR':
        return (
          <Chip size="small" label="Error" color="error" variant="outlined" />
        );
      default:
        return <Chip size="small" label={status} variant="outlined" />;
    }
  };

  // In UPDATE mode, hide rows with no changes — they would be no-op writes.
  // They default to SKIP (see handleValidate) so they are not committed.
  const isUpdateMode = validationResults?.mode === 'UPDATE';
  const rowHasChanges = (r) =>
    Array.isArray(r.changed_fields) && r.changed_fields.length > 0;
  const changedUpdateCount = isUpdateMode
    ? previewRows.filter((r) => r.status === 'WILL_UPDATE' && rowHasChanges(r))
        .length
    : validationResults?.summary?.will_update || 0;
  const unchangedUpdateCount = isUpdateMode
    ? previewRows.filter((r) => r.status === 'WILL_UPDATE' && !rowHasChanges(r))
        .length
    : 0;
  const visibleRows = isUpdateMode
    ? previewRows.filter((r) => r.status !== 'WILL_UPDATE' || rowHasChanges(r))
    : previewRows;

  // Ticket #257: rows that will switch ACC sync on or off. Counted over the rows
  // being committed, so a row the user switched to SKIP does not count.
  const accLinked = !!validationResults?.acc_asset_sync?.linked;
  const committedRows = previewRows.filter((r) => r.selectedAction !== 'SKIP');
  const accEnableCount = committedRows.filter(
    (r) => r.acc_sync_will_enable
  ).length;
  const accDisableCount = committedRows.filter(
    (r) => r.acc_sync_will_disable
  ).length;

  // Paginate the preview. Rendering a full table row (with a Select) for
  // thousands of rows at once freezes the browser — which is what makes the
  // spinner appear stuck on large files. Only the current page is rendered;
  // every row is still validated and imported on commit.
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pagedRows = visibleRows.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog
      open={open}
      onClose={loading || bulkDeleting ? undefined : handleClose}
      maxWidth={activeStep === 1 ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          overflowX: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CloudUploadIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Bulk Packages Import
          </Typography>
        </Box>
      </DialogTitle>

      <Box sx={{ width: '100%', mt: 3, px: 3, boxSizing: 'border-box' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ mt: 1, p: 3 }}>
        {error && (
          <Alert
            severity="error"
            variant="filled"
            sx={{ mb: 3, borderRadius: 1 }}
          >
            {error}
          </Alert>
        )}

        {/* STEP 0: Upload File */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              Upload a packages CSV file to bulk create or update project
              packages. The system will auto-detect the operational mode based
              on the headers:
            </Typography>

            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              <Typography variant="body2" component="div">
                • <strong>CREATE Mode</strong>: Triggered when the CSV has{' '}
                <strong>no `id` column</strong>. Requires package{' '}
                <code>description</code> and <code>area_name</code>. The{' '}
                <code>name</code> (short code) must be unique per project.
              </Typography>
              <Typography variant="body2" component="div">
                • <strong>UPDATE Mode</strong>: Triggered when the CSV has an{' '}
                <strong>`id` column</strong>. Updates existing packages by their
                ID. A blank cell preserves the existing value; enter{' '}
                <code>null</code> to explicitly clear a field.
              </Typography>
            </Box>

            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleUploadClick}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                p: 5,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragOver
                  ? 'action.hover'
                  : 'background.paper',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".csv"
                onChange={handleFileSelect}
              />
              <CloudUploadIcon
                sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.primary" gutterBottom>
                {file ? file.name : 'Drag and drop your CSV file here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB`
                  : 'or click to browse from computer'}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 3, borderRadius: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Important Notes & Formats:
              </Typography>
              <Typography variant="body2">
                - trade_name: Must match valid trades (Electrical, Pipefitting,
                Plumbing, Sheet Metal, Millwright) case-insensitively.
              </Typography>
              <Typography variant="body2">
                - pm_email: Must match an active system user's email.
              </Typography>
              <Typography variant="body2">
                - field_start_date: Use format <code>YYYY-MM-DD</code>.
              </Typography>
              <Typography variant="body2">
                - Numbers (hours, duration, units): Positive numeric values.
              </Typography>
              <Typography variant="body2">
                - general conditions: setting <code>is_general_conditions</code>{' '}
                to true forces include activities to false.
              </Typography>
              <Typography variant="body2">
                - acc_sync_enabled: <code>true</code>/<code>false</code>. Blank
                on a new package = on when the project has ACC asset sync (off
                for General Conditions and Change Order); blank on an update =
                no change. ACC assets are created in the background after the
                import.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* STEP 1: Preview & Resolve */}
        {activeStep === 1 && validationResults && (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              mb={3}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Validation Preview ({validationResults.mode} Mode)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review row details below. Configure actions for duplicates or
                  resolve errors.
                </Typography>
              </Box>

              <Box display="flex" gap={1}>
                {validationResults.summary.errors > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadErrorReport}
                  >
                    Download Error Report
                  </Button>
                )}
              </Box>
            </Box>

            {/* Summary Statistics Panel */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                backgroundColor: 'grey.50',
              }}
            >
              <Box flex={1} minWidth={100} textAlign="center">
                <Typography variant="caption" color="text.secondary" uppercase>
                  Total Rows
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {validationResults.summary.total_rows}
                </Typography>
              </Box>
              <Box flex={1} minWidth={100} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Will Create
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {validationResults.summary.will_create}
                </Typography>
              </Box>
              <Box flex={1} minWidth={100} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Will Update
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {changedUpdateCount}
                </Typography>
              </Box>
              <Box flex={1} minWidth={100} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Duplicate Names
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {validationResults.summary.duplicates}
                </Typography>
              </Box>
              <Box flex={1} minWidth={100} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Errors
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {validationResults.summary.errors}
                </Typography>
              </Box>
              {accLinked && (
                <Box flex={1} minWidth={100} textAlign="center">
                  <Typography variant="caption" color="text.secondary">
                    Will Sync to ACC
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {accEnableCount}
                  </Typography>
                </Box>
              )}
            </Paper>

            {accEnableCount > 0 && (
              <Alert
                icon={<InfoIcon />}
                severity="info"
                sx={{ mb: 3, borderRadius: 1.5 }}
              >
                ACC assets for <strong>{accEnableCount}</strong>{' '}
                {accEnableCount === 1 ? 'package' : 'packages'} will be created
                in the background after you commit. You can close this window
                while that runs.
              </Alert>
            )}

            {accDisableCount > 0 && (
              <Alert
                icon={<InfoIcon />}
                severity="info"
                sx={{ mb: 3, borderRadius: 1.5 }}
              >
                <strong>{accDisableCount}</strong>{' '}
                {accDisableCount === 1 ? 'package' : 'packages'} will stop
                syncing to ACC.
              </Alert>
            )}

            {validationResults.summary.errors > 0 && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 1.5 }}>
                CSV file contains {validationResults.summary.errors} rows with
                errors. These rows will be automatically skipped during
                processing.
              </Alert>
            )}

            {validationResults.new_areas &&
              validationResults.new_areas.length > 0 && (
                <Alert
                  icon={<InfoIcon />}
                  severity="info"
                  sx={{ mb: 3, borderRadius: 1.5 }}
                >
                  The following new areas will be automatically created:{' '}
                  <strong>{validationResults.new_areas.join(', ')}</strong>.
                </Alert>
              )}

            {validationResults.mode === 'UPDATE' && (
              <Alert
                icon={<InfoIcon />}
                severity="info"
                sx={{ mb: 3, borderRadius: 1.5 }}
              >
                A <strong>blank cell keeps the package's existing value</strong>{' '}
                — it is not cleared. To deliberately clear a field, enter{' '}
                <code>null</code> in that cell.
              </Alert>
            )}

            {isUpdateMode && unchangedUpdateCount > 0 && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 1.5 }}>
                Only rows with changes are shown.{' '}
                <strong>{unchangedUpdateCount}</strong>{' '}
                {unchangedUpdateCount === 1 ? 'package has' : 'packages have'}{' '}
                no changes and will be skipped.
              </Alert>
            )}

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 400, borderRadius: 1.5 }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="60">Row</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Details / Errors</TableCell>
                    <TableCell width="160">Action to Perform</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3 }}
                        >
                          No changes to apply — every row matches the existing
                          package values.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedRows.map((row) => (
                    <TableRow
                      key={row.row_number}
                      error={row.status === 'ERROR'}
                    >
                      <TableCell>{row.row_number}</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {row.name || (
                          <Typography variant="caption" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.area_name || (
                          <Typography variant="caption" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                        {row.area_is_new && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="info.main"
                            fontWeight="bold"
                          >
                            (New Area)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.package_identifier || row.data?.name || '-'}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {renderStatusChip(row.status)}
                          </Box>
                          {row.errors ? (
                            <Box sx={{ mt: 0.5 }}>
                              {row.errors.map((err, idx) => (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  display="block"
                                  color="error"
                                  sx={{ lineHeight: 1.2 }}
                                >
                                  • {err}
                                </Typography>
                              ))}
                            </Box>
                          ) : row.status === 'DUPLICATE' ||
                            row.status === 'WILL_UPDATE' ||
                            row.status === 'WILL_CREATE' ? (
                            <Box>
                              {row.changed_fields &&
                              row.changed_fields.length > 0 ? (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="bold"
                                    display="block"
                                  >
                                    {row.status === 'WILL_CREATE'
                                      ? 'Fields to set:'
                                      : 'Changes:'}
                                  </Typography>
                                  {row.changed_fields.map((change, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="caption"
                                      display="block"
                                      sx={{
                                        lineHeight: 1.2,
                                        color: 'info.main',
                                      }}
                                    >
                                      • <strong>{change.field}</strong>:{' '}
                                      {change.from} → {change.to}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {row.status === 'DUPLICATE'
                                    ? 'No other field changes'
                                    : row.status === 'WILL_CREATE'
                                      ? 'No fields provided'
                                      : 'No changes detected (all values match)'}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Ready
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={row.selectedAction}
                            onChange={(e) =>
                              handleRowActionChange(
                                row.row_number,
                                e.target.value
                              )
                            }
                            disabled={row.status === 'ERROR' || loading}
                            sx={{ height: 32, fontSize: '0.875rem' }}
                          >
                            {row.status === 'ERROR' ? (
                              <MenuItem value="SKIP">Skip (Error)</MenuItem>
                            ) : (
                              [
                                row.status === 'WILL_CREATE' && (
                                  <MenuItem key="create" value="CREATE">
                                    Create
                                  </MenuItem>
                                ),
                                row.status === 'WILL_UPDATE' && (
                                  <MenuItem key="update" value="UPDATE">
                                    Update
                                  </MenuItem>
                                ),
                                row.status === 'DUPLICATE' && [
                                  <MenuItem key="dup-update" value="UPDATE">
                                    Update Existing
                                  </MenuItem>,
                                  <MenuItem key="dup-create" value="CREATE">
                                    Create Duplicate
                                  </MenuItem>,
                                ],
                                <MenuItem key="skip" value="SKIP">
                                  Skip
                                </MenuItem>,
                              ]
                                .flat()
                                .filter(Boolean)
                            )}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {visibleRows.length > 0 && (
              <TablePagination
                component="div"
                count={visibleRows.length}
                page={safePage}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[50, 100, 200]}
                labelRowsPerPage="Rows per page:"
              />
            )}
          </Box>
        )}

        {/* STEP 2: Import Results */}
        {activeStep === 2 && commitResults && (
          <Box textAlign="center" py={2}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Import Completed Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              All requested database updates have been applied in a secure,
              single transaction.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 3,
                maxWidth: 500,
                mx: 'auto',
                mb: 4,
                display: 'flex',
                gap: 3,
                justifyContent: 'space-around',
                backgroundColor: 'grey.50',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Created Packages
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {commitResults.created}
                </Typography>
              </Box>
              <Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Updated Packages
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {commitResults.updated}
                </Typography>
              </Box>
              <Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Skipped / Ignored
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {commitResults.skipped}
                </Typography>
              </Box>
            </Paper>

            {commitResults.new_areas_created &&
              commitResults.new_areas_created.length > 0 && (
                <Box maxWidth={500} mx="auto" textAlign="left" mb={2}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    New Areas Created:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, maxHeight: 120, overflowY: 'auto' }}
                  >
                    {commitResults.new_areas_created.map((area, idx) => (
                      <Chip
                        key={idx}
                        label={area}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Paper>
                </Box>
              )}

            {/* Rows imported with a change left out, e.g. ACC sync not turned
                on because the project's asset sync was switched off. */}
            {commitResults.errors && commitResults.errors.length > 0 && (
              <Box maxWidth={500} mx="auto" textAlign="left" mb={2}>
                <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                  {commitResults.errors.map((message, idx) => (
                    <Typography key={idx} variant="body2">
                      {message}
                    </Typography>
                  ))}
                </Alert>
              </Box>
            )}

            {/* Packages this import brought to a zero budget (ticket #268) */}
            {zeroedPackages.length > 0 && (
              <Box maxWidth={500} mx="auto" textAlign="left" mb={2}>
                {bulkDeleteResult ? (
                  <Alert
                    severity={
                      bulkDeleteResult.blocked.length > 0 || envGuardSkipped > 0
                        ? 'warning'
                        : 'success'
                    }
                    sx={{ borderRadius: 1.5 }}
                  >
                    {bulkDeleteResult.deleted.length} of {zeroedPackages.length}{' '}
                    zero-budget packages deleted.
                    {bulkDeleteResult.blocked.length > 0 &&
                      ' The rest were not deleted:'}
                    {bulkDeleteResult.blocked.map((b) => (
                      <Typography key={b.id} variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{zeroedNameById.get(b.id) || b.id}</strong>
                        {' — '}
                        {b.reason}
                      </Typography>
                    ))}
                    {/* Dev/staging: linked assets stay live in ACC */}
                    {envGuardSkipped > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {envGuardSkipped === 1
                          ? '1 deleted package has an ACC asset that was not archived because ACC writes are turned off in this environment (development/staging).'
                          : `${envGuardSkipped} deleted packages have ACC assets that were not archived because ACC writes are turned off in this environment (development/staging).`}
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert
                    severity={bulkDeleteError ? 'error' : 'info'}
                    sx={{ borderRadius: 1.5 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setZeroedDialogOpen(true)}
                      >
                        {bulkDeleteError ? 'Try again' : 'Delete'}
                      </Button>
                    }
                  >
                    {bulkDeleteError ||
                      `${zeroedPackages.length} package${
                        zeroedPackages.length === 1 ? ' now has' : 's now have'
                      } zero budgeted hours.`}
                  </Alert>
                )}
              </Box>
            )}

            {accPending > 0 && (
              <AccAssetProgress
                accAssets={commitResults.acc_assets}
                status={accStatus}
                statusError={accStatusError}
                timedOut={accPollTimedOut}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {loading ? (
          <Box display="flex" alignItems="center" gap={2} px={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {activeStep === 0
                ? 'Parsing & Validating CSV...'
                : 'Applying changes to database...'}
            </Typography>
          </Box>
        ) : (
          <>
            {activeStep === 0 && (
              <>
                <Button onClick={handleClose} variant="text">
                  Cancel
                </Button>
                <Button
                  onClick={handleValidate}
                  variant="contained"
                  disabled={!file}
                  startIcon={<PlayArrowIcon />}
                >
                  Validate File
                </Button>
              </>
            )}

            {activeStep === 1 && (
              <>
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  startIcon={<ChevronLeftIcon />}
                >
                  Back
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={handleClose} variant="text" sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCommit}
                  variant="contained"
                  color="primary"
                  disabled={
                    !previewRows.some((r) => r.selectedAction !== 'SKIP')
                  }
                >
                  Commit Changes
                </Button>
              </>
            )}

            {activeStep === 2 && (
              <Button
                onClick={handleClose}
                variant="contained"
                color="success"
                disabled={bulkDeleting}
              >
                Finish & Refresh List
              </Button>
            )}
          </>
        )}
      </DialogActions>

      {/* Reason capture for budget changes (ticket #160 addendum). Renders in
          its own portal, stacked above this modal. */}
      <BudgetChangeReasonsDialog
        open={budgetDialogOpen}
        changes={pendingBudgetChanges}
        submitting={loading}
        error={budgetError}
        onCancel={handleBudgetDialogCancel}
        onApply={runCommit}
      />

      {/* "Delete these packages?" after the import zeroed their budgets
          (ticket #268). Stacked above this modal like the reasons dialog. */}
      <Dialog
        open={zeroedDialogOpen}
        onClose={bulkDeleting ? undefined : () => setZeroedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete These Packages?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {zeroedPackages.length === 1
              ? 'This package now has'
              : `These ${zeroedPackages.length} packages now have`}{' '}
            zero budgeted hours after this import. Delete{' '}
            {zeroedPackages.length === 1 ? 'it' : 'them'} now? Any ACC assets
            will be archived as well. Packages that still have tracked time are
            skipped.
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 1.5, mt: 1, maxHeight: 240, overflowY: 'auto' }}
          >
            {zeroedPackages.map((p) => (
              <Typography key={p.id} variant="body2">
                {zeroedPackageLabel(p)}
              </Typography>
            ))}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setZeroedDialogOpen(false)}
            disabled={bulkDeleting}
            autoFocus
          >
            No, keep them
          </Button>
          <Button
            onClick={handleBulkDeleteZeroed}
            color="error"
            variant="contained"
            disabled={bulkDeleting}
            startIcon={
              bulkDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {bulkDeleting ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
